<template>
  <v-wait :for="loaderId">
    <template slot="waiting">
      <HalfCircleSpinner />
      <div class="spinner-message my-3 text-center">
        Loading Ideal Career Opportunities...
      </div>
    </template>
    <b-card
      class="rounded-0 shadow-none phairify-card mb-2"
      header-class="phairify-card-header rounded-0 border-0"
      body-class="px-1 px-md-2 px-lg-4 pt-4 pb-3"
    >
      <b-row align-v="center" slot="header">
        <b-col
          cols="12"
          lg="6"
          class="d-flex d-flex flex-column flex-lg-row align-items-start"
        >
          <div class="d-inline-flex mr-md-3 align-items-center">
            <i
              class="mdi mdi-newspaper-variant-multiple-outline"
              aria-hidden="true"
            />
            <h2 class="phairify-card-title mb-0 phairify-color-navy pl-2">
              <strong>Ideal Career Opportunities</strong>
              <strong v-if="jobProfiles.length > 0">
                ({{ jobProfiles.length }})</strong
              >
            </h2>
          </div>
          <div class="d-inline-flex align-items-center">
            <i class="mdi mdi-briefcase-check-outline mr-1" />
            {{ totalMatches }}
            {{ totalMatches === 1 ? 'Match' : 'Matches' }}
            ({{ totalPerfectMatches }} perfect, {{ totalCoreMatches }} core)
          </div>
        </b-col>
        <b-col
          cols="12"
          lg="6"
          class="d-flex flex-column flex-lg-row justify-content-lg-end align-items-center my-2 my-lg-0 px-0 pl-2 pl-lg-0"
          align-self="stretch"
        >
          <ico-alerts-button-modal class="mr-2" v-if="isIcoAlertsVisible" />
          <span id="create_btn_wrapper">
            <b-button
              class="phairify-card-header__button px-5"
              variant="v2_primary"
              @click="onNewJobProfile"
            >
              <template v-if="!isCreateLoading">Create ICO</template>
              <template v-else>
                <b-spinner small />
              </template>
            </b-button>
          </span>
        </b-col>
      </b-row>
      <b-row v-if="(jobProfiles || []).length === 0">
        <b-col cols="12" class="text-center">
          <slot name="empty-list-text">
            <span>
              You have not created an ICO yet. Create one by clicking the
              ‘Create ICO’ button above.
            </span>
          </slot>
        </b-col>
      </b-row>
      <div v-else>
        <div v-for="profile in jobProfiles" :key="profile.id">
          <job-profile-card
            :profile="profile"
            :job-questions="jobQuestions"
            @delete-job-profile="onDeleteJobProfile"
          />
        </div>
      </div>
    </b-card>
  </v-wait>
</template>

<script>
import JobProfileCard from '@/components/exchange/JobProfileCard';
import { HalfCircleSpinner } from 'epic-spinners';
import IcoAlertsButtonModal from '@/components/dashboard/ico-alerts/IcoAlertsButtonModal';
import {
  CREATE_JOB_PROFILE_ACTION,
  DELETE_JOB_PROFILE_ACTION,
  GET_JOB_PROFILES_ACTION,
} from '@/store/jobProfiles';
import { handleServerErrorWithValidation } from '@/helpers/ToastedHelper';
import Api from "@/providers/Api";

export default {
  name: 'JobProfilesList',
  components: {
    HalfCircleSpinner,
    JobProfileCard,
    IcoAlertsButtonModal,
  },
  created: function () {
    this.uploadJobProfiles();
  },
  data: function () {
    return {
      isCreateLoading: false,
      jobQuestions: [],
    };
  },
  computed: {
    jobProfiles: function () {
      return this.$store.state.jobProfiles.items;
    },
    loaderId: function () {
      return `loading_${this.$options.name}`;
    },
    isIcoAlertsVisible: function () {
      return [
        'dev.phairify.com',
        'demo.phairify.com',
        'localhost:8080',
      ].includes(window.location.host);
    },
    isDemoMode: function () {
      return this.$store.getters.isDemoMode || this.$store.state.user.is_demo;
    },
    totalMatches: function () {
      return this.jobProfiles.reduce((total, profile) => {
        total += profile.jobProfileJobOfferPerfectMatchesCount || 0;
        total += profile.jobProfileJobOfferCoreMatchesCount || 0;
        return total;
      }, 0);
    },
    totalCoreMatches: function () {
      return this.jobProfiles.reduce((total, profile) => {
        total += profile.jobProfileJobOfferCoreMatchesCount || 0;
        return total;
      }, 0);
    },
    totalPerfectMatches: function () {
      return this.jobProfiles.reduce((total, profile) => {
        total += profile.jobProfileJobOfferPerfectMatchesCount || 0;
        return total;
      }, 0);
    },
  },
  methods: {
    uploadJobProfiles: async function () {
      try {
        this.$wait.start(this.loaderId);
        this.jobQuestions = await Api.getAllActiveJobQuestions();
        await this.$store.dispatch(GET_JOB_PROFILES_ACTION);
      } catch (e) {
        this.$toasted.error('Unable to upload data, please try again later', {
          duration: 3000,
        });
      } finally {
        this.$wait.end(this.loaderId);
      }
    },
    onNewJobProfile: async function () {
      this.isCreateLoading = true;
      try {
        const newProfile = await this.$store.dispatch(
          CREATE_JOB_PROFILE_ACTION
        );
        this.$router.push({
          name: `jobProfilePage`,
          params: {
            isNew: true,
            id: (newProfile || {}).id,
          },
        });
      } catch (error) {
        handleServerErrorWithValidation(error, this.$toasted);
      } finally {
        this.isCreateLoading = false;
      }
    },
    onDeleteJobProfile: async function (id) {
      try {
        this.$wait.start(this.loaderId);
        await this.$store.dispatch(DELETE_JOB_PROFILE_ACTION, id);
      } catch (e) {
        handleServerErrorWithValidation(e, this.$toasted);
      } finally {
        this.$wait.end(this.loaderId);
      }
    },
  },
};
</script>

<style scoped></style>
